// searchStore.ts
import { writable, get } from "svelte/store";
import { browser } from "$app/environment";
import type { SearchResult, SearchInfo, MatchStatus, SearchOptions, Lead, CriteriaV3_5 } from "$lib/types";
import { MATCH_STATUS, SEARCH_PROGRESS } from "$lib/types";
import { SearchAPI } from "$lib/api/search";
import { SearchEventHandler as basicSearchEventHandler } from "$lib/api/events/basicSearchEventHandler";
import { ExpandSearchHandler as expandSearchEventHandler } from "$lib/api/events/expandSearchEventHandler";
import { searchHistoryStore } from "./searchHistoryStore";
import { goto } from "$app/navigation";
import type { SearchProgress } from "$lib/types";
import { searchPreferences, authStore } from "$lib/stores";

const DEFAULT_MODEL = { value: "gpt-4o-mini", label: "gpt-4o-mini" };
const SEARCH_PREFIX = "search_";
export const FIND_MORE_PAGES_NUM = 30;
export const SEARCH_RESULTS_BATCH_SIZE_NO_USER = 5;
export const SEARCH_RESULTS_BATCH_SIZE = 40;
export const SEARCH_RESULTS_BATCH_SIZE_UNLIMITED = 0;
export const SEARCH_RESULTS_BATCH_SIZE_V_3_5 = 0;
export const CUSTOM_SLUG_SUFFIX = "-c";
export const ANONYMOUS_USER_ID = "9997";

export function searchResultToLead(result: SearchResult): Lead {
  return {
    id: result.id,
    firstName: result.firstName,
    lastName: result.lastName,
    email: `${result.firstName.toLowerCase()}.${result.lastName.toLowerCase()}@${result.domain}`,
    companyName: result.companyName,
    companyWebsite: result.companyWebsite,
    title: result.title,
    linkedinURL: result.linkedinURL,
    domain: result.domain,
    searchResultId: result.id,
  };
}

function createSearchStore() {
  const { subscribe, update } = writable<{ [slug: string]: SearchInfo }>({});
  const activeStreams: { [slug: string]: EventSource } = {};
  const currentSlugSearchHandlers: {
    [slug: string]:
      | ReturnType<typeof basicSearchEventHandler.setupSearchEventHandlers>
      | ReturnType<typeof expandSearchEventHandler.setupExpandSearchHandlers>;
  } = {};

  function mergeResults(slug: string, newResultsBatch: SearchResult[], options?: { replaceResults?: boolean }) {
    if (!newResultsBatch.length) return;

    update((store) => {
      const search = store[slug];
      if (!search) return store;

      // If replacing, just use new batch, otherwise combine with existing
      const mergedResults = options?.replaceResults
        ? [...newResultsBatch]
        : [...(search.results || []), ...newResultsBatch];

      const uniqueResults = Array.from(new Map(mergedResults.map((item) => [item.id, item])).values());

      const updatedSearch = {
        ...search,
        results: uniqueResults,
        definiteMatch: search.definiteMatch,
        partialMatch: search.partialMatch,
      };

      searchHistoryStore.updateSearch(slug, {
        definiteMatch: search.definiteMatch || 0,
        partialMatch: search.partialMatch || 0,
        matchesCount: (search.definiteMatch || 0) + (search.partialMatch || 0),
      });

      return {
        ...store,
        [slug]: updatedSearch,
      };
    });
  }

  function isSearchActive(slug: string): boolean {
    const search = get({ subscribe })[slug];

    const value = !!(search && activeStreams[slug] && activeStreams[slug].readyState !== EventSource.CLOSED);

    return value;
  }

  function handleDisconnect(slug: string) {
    update((states) => {
      const currentState = states[slug];
      if (!currentState || currentState.progressStatus !== SEARCH_PROGRESS.IN_PROGRESS) {
        return states;
      }

      const updatedState = {
        ...currentState,
        progressStatus: SEARCH_PROGRESS.CANCELLED,
        error: currentState.error,
      };

      return { ...states, [slug]: updatedState };
    });

    cleanup(slug);
  }

  function getNewTimestamp(): string {
    const date = new Date();
    date.setDate(date.getDate() + 1);
    return date.toISOString();
  }

  function cleanup(slug: string) {
    if (activeStreams[slug]) {
      activeStreams[slug].close();
      delete activeStreams[slug];
    }
    if (currentSlugSearchHandlers[slug]) {
      delete currentSlugSearchHandlers[slug];
    }
  }

  function updateSearch(slug: string, updates: Partial<SearchInfo>) {
    update((store) => {
      const existingSearch = store[slug];
      if (!existingSearch) return store;

      const updatedSearch = {
        ...existingSearch,
        ...updates,
      };

      return {
        ...store,
        [slug]: updatedSearch,
      };
    });
  }

  async function expandSearch(params: { searchID: string; slug: string; userId?: string; findAll?: boolean }) {
    const { searchID, slug, userId, findAll } = params;
    logger.log("Expand search triggered for:", slug);

    if (activeStreams[slug]) {
      logger.log("Active search exists, skipping expand:", slug);
      return;
    }

    const version = getSearchVersion();
    const batchSize = findAll
      ? String(SEARCH_RESULTS_BATCH_SIZE_UNLIMITED)
      : userId
        ? String(SEARCH_RESULTS_BATCH_SIZE)
        : String(SEARCH_RESULTS_BATCH_SIZE_NO_USER);

    try {
      // Update search status to expanding
      update((states) => ({
        ...states,
        [slug]: {
          ...states[slug],
          progressStatus: SEARCH_PROGRESS.IN_PROGRESS,
          searchStatus: "expanding",
        },
      }));

      // Start the expansion
      try {
        const expansionResponse = await SearchAPI.startExpand({
          searchId: searchID,
          definiteMatchCount: batchSize,
          version,
        });

        logger.log("Expansion started successfully:", expansionResponse);

        // Create event source using the expand-specific endpoint
        const eventSource = SearchAPI.createExpandSearchEventSource(searchID, userId);
        activeStreams[slug] = eventSource;

        // Set up handlers
        const handlers = expandSearchEventHandler.setupExpandSearchHandlers({
          eventSource,
          slug,
          updateStoreFn: update,
          endSearchFn: endSearch,
          saveSearchToLocalStorage,
          processSearchResult,
          activeStreams,
        });

        currentSlugSearchHandlers[slug] = handlers;
      } catch (_error) {
        logger.log("Failed to establish expand stream, falling back to direct fetch");
        const results = await SearchAPI.getSearchResultsByID(
          searchID,
          getResults(slug).length,
          SEARCH_RESULTS_BATCH_SIZE,
          true,
          userId,
        );

        if (results.length > 0) {
          mergeResults(slug, results);
        }

        // saveSearchToLocalStorage(get({ subscribe })[slug]);
        endSearch(slug);
      }
    } catch (error) {
      console.error("Error during search expansion:", error);
      setError(slug, error instanceof Error ? error.message : "Search expansion failed");
      cleanup(slug);
    }
  }

  function getSearchVersion(): string {
    return searchPreferences.getSearchAlgoVersion();
  }

  async function expandSearchWithoutEvents(searchID: string, slug: string, userId?: string) {
    logger.log("Expand search triggered for:", slug);

    if (activeStreams[slug]) {
      logger.log("Active search exists, skipping expand:", slug);
      return;
    }

    const version = getSearchVersion();
    const batchSize =
      version === "v3.5"
        ? String(SEARCH_RESULTS_BATCH_SIZE)
        : userId
          ? String(SEARCH_RESULTS_BATCH_SIZE)
          : String(SEARCH_RESULTS_BATCH_SIZE_NO_USER);

    try {
      update((states) => ({
        ...states,
        [slug]: {
          ...states[slug],
          progressStatus: SEARCH_PROGRESS.IN_PROGRESS,
          searchStatus: "expanding",
        },
      }));

      try {
        const expansionResponse = await SearchAPI.startExpand({
          searchId: searchID,
          definiteMatchCount: batchSize,
          version,
        });

        logger.log("Expansion started successfully:", expansionResponse);
      } catch (_error) {
        logger.log("Failed to establish expand stream, falling back to direct fetch");
        // const results = await SearchAPI.getSearchResultsByID(
        //   searchID,
        //   getResults(slug).length,
        //   SEARCH_RESULTS_BATCH_SIZE,
        //   true,
        //   userId,
        // );

        // if (results.length > 0) {
        //   mergeResults(slug, results);
        // }

        // saveSearchToLocalStorage(get({ subscribe })[slug]);
        endSearch(slug);
      }
    } catch (error) {
      console.error("Error during search expansion:", error);
      setError(slug, error instanceof Error ? error.message : "Search expansion failed");
      cleanup(slug);
    }
  }

  function endSearch(slug: string) {
    logger.log("Ending search for:", slug);
    try {
      cleanup(slug);

      update((states) => {
        const updatedState = {
          ...states[slug],
          progressStatus: SEARCH_PROGRESS.FINISHED,
          searchStatus: "completed",
        };

        // Save final state to localStorage
        // saveSearchToLocalStorage(updatedState);

        searchHistoryStore.updateSearch(slug, {
          searchStatus: "completed",
        });

        return { ...states, [slug]: updatedState };
      });
    } catch (error) {
      console.error("Error ending search:", error);
    }
  }

  function saveSearchToLocalStorage(state: SearchInfo) {
    if (!browser) return;

    try {
      localStorage.setItem(`${SEARCH_PREFIX}${state.slug}`, JSON.stringify(state));
    } catch (error) {
      // console.error("Error saving to localStorage:", error);
      if (error instanceof DOMException && error.name === "QuotaExceededError") {
        handleStorageQuotaExceeded(state);
      }
    }
  }

  function handleStorageQuotaExceeded(state: SearchInfo) {
    Object.keys(localStorage)
      .filter((key) => key.startsWith(SEARCH_PREFIX))
      .sort((a, b) => {
        const timeA = getStorageItemTimestamp(a);
        const timeB = getStorageItemTimestamp(b);
        return timeA - timeB;
      })
      .some((key) => attemptToSaveAfterRemoval(key, state));
  }

  function getStorageItemTimestamp(key: string): number {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item).updatedAt || 0 : 0;
  }

  function attemptToSaveAfterRemoval(key: string, state: SearchInfo): boolean {
    localStorage.removeItem(key);
    try {
      localStorage.setItem(`${SEARCH_PREFIX}${state.slug}`, JSON.stringify(state));
      return true;
    } catch {
      return false;
    }
  }

  function setInitialState(query: string, slug: string, userID?: string, options?: SearchOptions): void {
    update((states) => ({
      ...states,
      [slug]: {
        id: "0",
        query,
        slug,
        error: null,
        results: [],
        timeToFirstMatch: null,
        criteria: [],
        keywords: [],
        category: "",
        model: DEFAULT_MODEL,
        totalResultsFound: 0,
        totalResultsGenerated: 0,
        progressStatus: SEARCH_PROGRESS.NOT_STARTED,
        searchStatus: mapProgressToSearchStatus(SEARCH_PROGRESS.NOT_STARTED),
        status: "visible",
        createdAt: getNewTimestamp(),
        updatedAt: getNewTimestamp(),
        definiteMatch: 0,
        partialMatch: 0,
        totalResults: 0,
        userID,
        mode: options?.mode || "default",
      },
    }));
  }

  /**
   * Set error state for a search
   */
  function setError(slug: string, error: string) {
    update((store) => {
      const existingSearch = store[slug];
      if (!existingSearch) return store;

      return {
        ...store,
        [slug]: {
          ...existingSearch,
          error,
          progressStatus: SEARCH_PROGRESS.CANCELLED,
          searchStatus: mapProgressToSearchStatus(SEARCH_PROGRESS.CANCELLED),
        },
      };
    });
  }

  async function fetchQueryMetadata(query: string, slug: string, _options?: { mode: string }) {
    const MAX_RETRIES = 5;
    const BASE_DELAY = 1000; // 1 second

    let attempt = 0;

    while (attempt <= MAX_RETRIES) {
      try {
        const state = get({ subscribe })[slug];
        const modelValue = state?.mode === "pro" ? "gpt-4o" : state?.model?.value || DEFAULT_MODEL.value;

        logger.log(
          `Attempt ${attempt + 1}/${MAX_RETRIES + 1} - Fetching metadata for:`,
          query,
          "with model:",
          modelValue,
        );

        const version = getSearchVersion();

        const metaData = await SearchAPI.fetchQueryMetadata({ query, modelValue, version });

        metaData.model = metaData.model || DEFAULT_MODEL;

        logger.log("Query metadata fetched:", metaData);

        update((states) => ({
          ...states,
          [slug]: {
            ...states[slug],
            criteria: metaData.criteria || [],
            keywords: metaData.keywords || [],
            category: metaData.searchType || "",
            numPages: metaData.numPages || 1,
            // definiteMatchCount: metaData.definiteMatchCount,
            // model: metaData.model,
          },
        }));

        searchHistoryStore.updateSearch(slug, {
          category: metaData.searchType || "",
        });

        logger.log("Query metadata fetched and updated:", metaData);
        return metaData;
      } catch (error) {
        attempt++;

        if (attempt > MAX_RETRIES) {
          console.error("Max retries reached. Error fetching metadata:", error);
          setError(slug, "Failed to fetch metadata after multiple attempts");
          return null;
        }

        // Calculate delay with exponential backoff
        const delay = BASE_DELAY * Math.pow(2, attempt - 1);

        // Add some randomness to prevent thundering herd
        const jitter = Math.random() * 1000;
        const totalDelay = delay + jitter;

        logger.log(`Attempt ${attempt}/${MAX_RETRIES} failed. Retrying in ${Math.round(totalDelay / 1000)}s...`);

        // Wait before next retry
        await new Promise((resolve) => setTimeout(resolve, totalDelay));
      }
    }

    // This line should never be reached due to the return null in the catch block,
    // but TypeScript might want it
    return null;
  }

  function getSearchFromLocalStorage(slug: string): SearchInfo | null {
    if (!browser) return null;

    try {
      const savedSearch = localStorage.getItem(`${SEARCH_PREFIX}${slug}`);
      return savedSearch ? JSON.parse(savedSearch) : null;
    } catch (error) {
      console.error("Error parsing saved search from localStorage:", error);
      return null;
    }
  }

  function createSlug(query: string): string {
    const baseSlug = query
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^\w-]+/g, "");
    const hash = Math.random().toString(36).substring(2, 8);
    return `${baseSlug}-${hash}${CUSTOM_SLUG_SUFFIX}`;
  }

  async function performSearch(
    params: {
      query: string;
      initialSlug: string;
      userID?: string;
      criteria?: string[];
      findAll?: boolean;
    },
    options?: SearchOptions,
  ) {
    const { query, initialSlug, userID, findAll } = params;
    if (shouldSkipSearch(initialSlug)) return;

    const version = getSearchVersion();
    const batchSize =
      findAll || version == "v3.5"
        ? String(SEARCH_RESULTS_BATCH_SIZE_UNLIMITED)
        : userID
          ? String(SEARCH_RESULTS_BATCH_SIZE)
          : String(SEARCH_RESULTS_BATCH_SIZE_NO_USER);

    logger.log("Performing search with params:", params);
    logger.log("Batch size:", batchSize);

    setInitialState(query, initialSlug, userID, options);

    // Navigate immediately with the temporary slug
    if (browser) {
      const pathname = window.location.pathname;
      const basePath = pathname.startsWith("/search-v2/") ? "/search-v2/" : "/search/";
      await goto(`${basePath}${initialSlug}`, { replaceState: false }); // Don't replace state here
    }

    try {
      // Update initial state to show processing
      update((currentStates) => ({
        ...currentStates,
        [initialSlug]: {
          ...currentStates[initialSlug],
          progressStatus: SEARCH_PROGRESS.IN_PROGRESS,
          searchStatus: "processing",
        },
      }));

      // const metadata = await fetchQueryMetadata(query, initialSlug);
      // if (!metadata) {
      //   setError(initialSlug, "Failed to start the search. Please try again.");
      //   return;
      // }
      const version = authStore.isLoggedIn() ? get(searchPreferences)?.searchAlgoVersion || "v3" : "v3";

      const saveSearchResponse = await SearchAPI.start({
        query: query,
        criteria: params.criteria || [],
        keywords: [],
        category: "",
        mode: options?.mode,
        pageNum: findAll ? 99 : 4,
        definiteMatchCount: String(batchSize),
        userID: userID,
        version,
      });

      // Update store with real search ID but keep the temporary slug for now
      const realSlug = saveSearchResponse.search_slug;
      const realID = saveSearchResponse.search_id;

      // Update the existing state instead of creating new
      update((currentStates) => {
        const currentState = currentStates[initialSlug];
        if (!currentState) return currentStates;

        const updatedState = {
          ...currentState,
          id: realID,
          progressStatus: SEARCH_PROGRESS.IN_PROGRESS,
          searchStatus: "processing",
          criteria: metadata.criteria || [],
          keywords: metadata.keywords || [],
          category: metadata.searchType || "",
        };

        return {
          ...currentStates,
          [initialSlug]: updatedState,
        };
      });

      // Save to localStorage with initial slug
      // saveSearchToLocalStorage(get({ subscribe })[initialSlug]);

      // Start search stream with real ID but keep using initial slug
      const eventSource = SearchAPI.createSearchEventSource(saveSearchResponse.search_id, userID);
      activeStreams[initialSlug] = eventSource;

      const handlers = basicSearchEventHandler.setupSearchEventHandlers({
        eventSource,
        slug: initialSlug,
        initialSlug: initialSlug,
        updateStoreFn: update,
        endSearchFn: endSearch,
        saveSearchToLocalStorage,
        processSearchResult,
        activeStreams,
      });

      currentSlugSearchHandlers[initialSlug] = handlers;

      // After everything is set up, update the URL silently
      if (browser) {
        const pathname = window.location.pathname;
        const basePath = pathname.startsWith("/search-v2/") ? "/search-v2/" : "/search/";
        history.replaceState(null, "", `${basePath}${realSlug}`);
      }
    } catch (error) {
      console.error("Error during search:", error);
      setError(initialSlug, error instanceof Error ? error.message : "Search failed");
    }
  }

  async function performSearchWithoutEvents(
    params: {
      query: string;
      initialSlug: string;
      userID?: string;
      criteria?: string[];
      findAll?: boolean;
    },
    options?: SearchOptions,
  ) {
    const { query, initialSlug, userID, findAll } = params;
    // if (shouldSkipSearch(initialSlug)) return;
    const version = getSearchVersion();
    const batchSize =
      findAll || version == "v3.5"
        ? String(SEARCH_RESULTS_BATCH_SIZE_UNLIMITED)
        : userID
          ? String(SEARCH_RESULTS_BATCH_SIZE)
          : String(SEARCH_RESULTS_BATCH_SIZE_NO_USER);

    setInitialState(query, initialSlug, userID, options);

    try {
      update((states) => ({
        ...states,
        [initialSlug]: {
          ...states[initialSlug],
          progressStatus: SEARCH_PROGRESS.IN_PROGRESS,
          searchStatus: "processing",
        },
      }));

      // const metadata = await fetchQueryMetadata(query, initialSlug);
      // if (!metadata) {
      //   setError(initialSlug, "Failed to fetch search metadata");
      //   return;
      // }

      // Save the search first
      const saveSearchResponse = await SearchAPI.start({
        query,
        // criteria: metadata.criteria as CriteriaV3_5[],
        // keywords: metadata.keywords,
        // category: metadata.searchType,
        criteria: params.criteria || [],
        keywords: [],
        category: "",
        mode: options?.mode,
        pageNum: findAll ? FIND_MORE_PAGES_NUM : 4,
        definiteMatchCount: batchSize,
        userID,
        version,
      });

      // Update store with real search ID but keep the temporary slug
      const realSlug = saveSearchResponse.search_slug;
      const realID = saveSearchResponse.search_id;

      // Remove old state and create new one with real slug
      update((states) => {
        const currentState = states[initialSlug];
        if (!currentState) return states;

        const updatedState = {
          ...currentState,
          id: realID,
          slug: realSlug,
          progressStatus: SEARCH_PROGRESS.IN_PROGRESS,
          searchStatus: "processing",
          criteria: [],
          keywords: [],
          category: "",
        };

        // Remove old state and add new one with real slug
        const { [initialSlug]: _, ...rest } = states;

        // Save the new state to localStorage
        // saveSearchToLocalStorage(updatedState);

        // Update history store
        searchHistoryStore.updateSearch(initialSlug, {
          slug: realSlug,
          category: "",
          id: realID,
        });

        if (browser) {
          logger.log("Redirecting to:", realSlug);
          const pathname = window.location.pathname;
          const basePath = pathname.startsWith("/search-dev/") ? "/search-dev/" : "/search/";
          logger.log("Full path:", `${basePath}${realSlug}`);
          goto(`${basePath}${realSlug}`, { replaceState: true });
        }

        return { ...rest, [realSlug]: updatedState };
      });
      return realSlug;
    } catch (error) {
      console.error("Error during search:", error);
      setError(initialSlug, error instanceof Error ? error.message : "Search failed");
      return "error";
    }
  }

  function getResults(slug: string): SearchResult[] {
    return get({ subscribe })[slug]?.results || [];
  }

  function shouldSkipSearch(slug: string): boolean {
    const existingSearch = get({ subscribe })[slug];
    // if (existingSearch?.progressStatus === SEARCH_PROGRESS.FINISHED) {
    //   logger.log("Search already finished, skipping new search:", slug);
    //   return true;
    // }

    // const historySearch = get(searchHistoryStore).find((s) => s.slug === slug);
    // if (historySearch?.status !== SEARCH_PROGRESS.NOT_STARTED) return true;
    if (activeStreams[slug]) return true;

    return false;
  }

  function processSearchResult(slug: string, result: SearchResult) {
    if (!isValidSearchResult(result)) return;

    update((states) => {
      const currentState = states[slug];
      if (!currentState) return states;

      const updatedState = addResultToSearch(currentState, result);
      updatedState.progressStatus = SEARCH_PROGRESS.IN_PROGRESS;
      // saveSearchToLocalStorage(updatedState);
      // searchHistoryStore.updateSearch(slug, { matchesCount: updatedState.totalResultsFound });

      logger.log("Updated state for search:", slug, "with new result:", result);
      return { ...states, [slug]: updatedState };
    });
  }

  function isValidSearchResult(result: SearchResult): boolean {
    return !!(
      (result.firstName || result.lastName || result.companyName) &&
      [
        MATCH_STATUS.DEFINITE_MATCH,
        MATCH_STATUS.PARTIAL_MATCH,
        MATCH_STATUS.NOT_MATCH,
        MATCH_STATUS.NEED_MORE_INFO,
      ].includes(result.match as MatchStatus)
    );
  }

  function isSameResult(r1: SearchResult, r2: SearchResult): boolean {
    if (!r1 || !r2) return false;
    if (!r1.id || !r2.id) return false;
    return r1.id === r2.id;
  }

  function addResultToSearch(currentState: SearchInfo, result: SearchResult): SearchInfo {
    const existingResults = currentState.results || [];
    if (existingResults.some((r) => isSameResult(r, result))) {
      return currentState;
    }
    const newResults = [...existingResults, result];

    const newDefinite = newResults.filter((r) => r.match === MATCH_STATUS.DEFINITE_MATCH).length || 0;
    const newPartial = newResults.filter((r) => r.match === MATCH_STATUS.PARTIAL_MATCH).length || 0;
    const newTotal = newDefinite + newPartial;

    return {
      ...currentState,
      results: newResults,
      definiteMatch: newDefinite > (currentState.definiteMatch || 0) ? newDefinite : currentState.definiteMatch,
      partialMatch: newPartial > (currentState.partialMatch || 0) ? newPartial : currentState.partialMatch,
      // totalLLMCost: (currentState.totalLLMCost || 0) + (result.llmCost || 0),
      // totalResultsGenerated: newTotal > currentsState.totalResultsFound ? newTotal : currentState.totalResultsFound,

      totalResultsFound: newTotal > (currentState.totalResultsFound || 0) ? newTotal : currentState.totalResultsFound,
    };
  }

  function mapProgressToSearchStatus(progressStatus: SearchProgress): string {
    switch (progressStatus) {
      case SEARCH_PROGRESS.IN_PROGRESS:
        return "processing";
      case SEARCH_PROGRESS.FINISHED:
        return "completed";
      case SEARCH_PROGRESS.CANCELLED:
        return "completed";
      default:
        return "created";
    }
  }

  function updateSearchResult(
    slug: string,
    resultId: string,
    updates: Partial<SearchResult>,
    options: {
      skipRefresh?: boolean;
    } = {},
  ) {
    if (options.skipRefresh) {
      const search = get({ subscribe })[slug];
      if (!search?.results) return;

      const result = search.results.find((r) => r.id === resultId);
      if (!result) return;

      Object.assign(result, updates);
      return;
    }

    update((store) => {
      const search = store[slug];
      if (!search?.results) return store;

      const results = search.results.map((r) => (r.id === resultId ? { ...r, ...updates } : r));

      return {
        ...store,
        [slug]: {
          ...search,
          results,
        },
      };
    });
  }

  function updateResults(slug: string, newResults: SearchResult[]) {
    mergeResults(slug, newResults, { replaceResults: true });
  }

  function prepareRerunSearch(oldSlug: string, newCriteria: string[]): string | null {
    const existingSearch = get({ subscribe })[oldSlug];
    if (!existingSearch) {
      console.error("No existing search found for slug:", oldSlug);
      return null;
    }

    // Create a new temporary slug for the rerun search
    const newSlug = createSlug(existingSearch.query);

    // Initialize the new search state with metadata from the existing search
    setInitialState(existingSearch.query, newSlug, existingSearch.userID, {
      mode: existingSearch.mode as "pro" | "default" | undefined,
    });

    // Update the store with the new search metadata
    update((states) => ({
      ...states,
      [newSlug]: {
        ...states[newSlug],
        criteria: newCriteria, // Use the new criteria directly, since it's already deduplicated
        keywords: existingSearch.keywords || [],
        category: existingSearch.category || "",
        numPages: existingSearch.numPages || 1,
        model: existingSearch.model || DEFAULT_MODEL,
      },
    }));

    return newSlug;
  }

  /**
   * Initialize or update a search with new metadata
   */
  function initializeSearch(slug: string, metadata: Partial<SearchInfo>) {
    update((store: { [slug: string]: SearchInfo }) => {
      const existingSearch = store[slug];
      // If search exists, merge metadata with existing data
      if (existingSearch) {
        return {
          ...store,
          [slug]: {
            userID: existingSearch.userID || "0",
            ...existingSearch,
            ...metadata,
            results: existingSearch.results || [],
            error: null,
          },
        };
      }

      // If new search, create with defaults
      return {
        ...store,
        [slug]: {
          id: "",
          slug,
          query: "",
          progressStatus: SEARCH_PROGRESS.UNDEFINED,
          searchStatus: "created",
          results: [],
          error: null,
          criteria: [],
          keywords: [],
          category: "",
          model: { value: "", label: "" },
          definiteMatch: 0,
          partialMatch: 0,
          totalResults: 0,
          userID: 0,
          ...metadata,
        } as SearchInfo,
      };
    });
  }

  return {
    subscribe,
    performSearch,
    performSearchWithoutEvents,
    reconnectToSearch: async (searchId: string, slug: string, userId?: string) => {
      const eventSource = SearchAPI.createSearchEventSource(searchId, userId);
      activeStreams[slug] = eventSource;

      const handlers = basicSearchEventHandler.setupSearchEventHandlers({
        eventSource,
        slug,
        initialSlug: slug,
        updateStoreFn: update,
        endSearchFn: endSearch,
        saveSearchToLocalStorage,
        processSearchResult,
        activeStreams,
      });

      currentSlugSearchHandlers[slug] = handlers;
    },
    reconnectToExpandSearch: async (searchId: string, slug: string, userId?: string) => {
      const eventSource = SearchAPI.createExpandSearchEventSource(searchId, userId);
      activeStreams[slug] = eventSource;

      const handlers = expandSearchEventHandler.setupExpandSearchHandlers({
        eventSource,
        slug,
        updateStoreFn: update,
        endSearchFn: endSearch,
        saveSearchToLocalStorage,
        processSearchResult,
        activeStreams,
      });

      currentSlugSearchHandlers[slug] = handlers;
    },
    expandSearch,
    expandSearchWithoutEvents,
    fetchQueryMetadata,
    setInitialState,
    setError,
    updateSearchResult,
    setQuery: (query: string, slug: string) =>
      update((states) => ({
        ...states,
        [slug]: { ...states[slug], query, slug },
      })),
    loadSearchBySlug: (slug: string) => {
      const savedSearch = getSearchFromLocalStorage(slug);
      if (savedSearch) {
        update((states) => ({
          ...states,
          [slug]: {
            ...savedSearch,
            error: null,
          },
        }));
        logger.log(`Loaded search with slug "${slug}" from localStorage:`, savedSearch);
        return true;
      }
      return false;
    },
    createSlug,
    stopSearch: (slug: string) => {
      cleanup(slug);
      update((states) => ({
        ...states,
        [slug]: {
          ...states[slug],
          progressStatus: SEARCH_PROGRESS.CANCELLED,
          searchStatus: mapProgressToSearchStatus(SEARCH_PROGRESS.CANCELLED),
        },
      }));
    },
    getResults,
    cleanup,
    getSearch: (slug: string) => get({ subscribe })[slug],
    // getSearchModel: (slug: string) => new SearchModel(get({ subscribe })[slug]),
    isSearchActive,
    handleDisconnect,
    mergeResults,
    updateResults,
    initializeSearch,
    updateSearch,
    mapProgressToSearchStatus,
    prepareRerunSearch,
  };
}

export const searchStore = createSearchStore();
